import { useEffect } from "react";
import { createPortal } from "react-dom";
import type { ReactNode } from "react";
import styles from "./Dialog.module.css";

interface DialogProps {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  /** Footer actions (typically a secondary + primary Button). */
  footer?: ReactNode;
  children: ReactNode;
}

/**
 * A modal dialog (style-guide.md: Modals). Renders through a portal, dims and
 * blurs the backdrop, traps nothing fancy but closes on Escape and on backdrop
 * click. Width is constrained 360–560px — do not build full-screen modals.
 */
function Dialog({ open, title, onClose, footer, children }: DialogProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className={styles.backdrop} onClick={onClose}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className={styles.title}>{title}</h2>
        <div className={styles.body}>{children}</div>
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

export default Dialog;
